import { encodeAttributesSearchParam } from "@common/hooks/filtering/attributesSearchParam";
import { Attribute } from "@common/hooks/filtering/sharedTypes";
import { partialFilterFor } from "@common/hooks/filtering/utils";
import { CrashType } from "@common/models/crashReporting";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath } from "react-router-dom";

/// Filter field names, as understood by `useFeedbackAttributes` and `useCrashesAttributes` —
/// both modules happen to use the same names. Spelled out here rather than imported from those
/// hooks so the Release module doesn't pull their page graphs into its chunk (and because the
/// crashes hook exports no field-name map at all).
const fieldNames = {
  appVersion: "app_version.full_version",
  priority: "priority",
  status: "status",
  reportType: "report_type",
};

export type TicketListFilters = {
  status?: TicketStatus;
  statusIsNot?: TicketStatus;
  priority?: TicketPriority;
  reportType?: CrashType;
};

export type TicketListLinkParams = {
  workspaceSlug: string;
  appKey: string;
  version?: string | null;
  filters: TicketListFilters;
};

/// Both list routes take exactly `workspaceSlug` + `appKey`; narrowing to them keeps
/// `generatePath` from demanding every param across the whole `RoutePaths` union.
type TicketListRoute = RoutePaths.USER_FEEDBACK | RoutePaths.CRASH_REPORTS;

/// Builds a link to a ticket list with filters already applied, so a release metric card can
/// hand off to the list showing exactly the tickets it counted.
function ticketListLinkFor(
  routePath: TicketListRoute,
  { workspaceSlug, appKey, version, filters }: TicketListLinkParams,
) {
  const attributes: Attribute[] = [];

  const addAttribute = (fieldName: string, fieldValue: string, operator: "is" | "is_not" | "starts_with" = "is") => {
    attributes.push({
      field_name: fieldName,
      filter: { ...partialFilterFor(operator), fieldValue },
      attributeOperator: attributes.length === 0 ? undefined : "and",
    });
  };

  if (version) addAttribute(fieldNames.appVersion, version, "starts_with");
  if (filters.priority) addAttribute(fieldNames.priority, filters.priority);
  if (filters.status) addAttribute(fieldNames.status, filters.status);
  if (filters.statusIsNot) addAttribute(fieldNames.status, filters.statusIsNot, "is_not");
  if (filters.reportType) addAttribute(fieldNames.reportType, filters.reportType);

  const path = generatePath(routePath, { workspaceSlug, appKey });
  if (attributes.length === 0) return path;

  /// The envelope is base64, so it has to be percent-encoded — an unescaped "+" would come
  /// back out of the query string as a space.
  return `${path}?search=${encodeURIComponent(encodeAttributesSearchParam(attributes))}`;
}

export function feedbackListLinkFor(params: TicketListLinkParams) {
  return ticketListLinkFor(RoutePaths.USER_FEEDBACK, params);
}

export function crashListLinkFor(params: TicketListLinkParams) {
  return ticketListLinkFor(RoutePaths.CRASH_REPORTS, params);
}
